// Offline safety net for billing.
// - Sales that cannot reach the server are queued in this browser and uploaded later.
// - Every sale carries a client_ref, so an upload that is retried is saved only once.
// - Products, offers and receipt settings are cached so billing works after a refresh.
import { api } from './api';

const QUEUE_KEY = 'icecream_offline_bills';
const CACHE_PREFIX = 'icecream_cache_';

const readQueue = () => {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch (e) { return []; }
};
const writeQueue = (q) => {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  window.dispatchEvent(new Event('offline-queue-changed'));
};

export const pendingBills = () => readQueue();

export const newClientRef = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `OFF-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}-${rand}`;
};

export function enqueueBill(entry) {
  const q = readQueue();
  if (!q.find((x) => x.client_ref === entry.client_ref)) q.push({ ...entry, tries: 0, last_error: null });
  writeQueue(q);
}

export function discardBill(ref) {
  writeQueue(readQueue().filter((x) => x.client_ref !== ref));
}

let syncing = false;
export async function syncPending() {
  if (syncing) return { synced: 0, busy: true };
  syncing = true;
  let synced = 0;
  try {
    for (const entry of readQueue()) {
      try {
        await api.post('/bills', { ...entry.body, client_ref: entry.client_ref, client_time: entry.client_time });
        writeQueue(readQueue().filter((x) => x.client_ref !== entry.client_ref));
        synced += 1;
      } catch (e) {
        if (e.offline) break; // still no connection: try again later
        const q = readQueue();
        const item = q.find((x) => x.client_ref === entry.client_ref);
        if (item) { item.tries = (item.tries || 0) + 1; item.last_error = e.message; }
        writeQueue(q);
      }
    }
  } finally {
    syncing = false;
  }
  return { synced };
}

export const cacheSet = (key, value) => {
  try { localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value)); } catch (e) { /* storage full */ }
};
export const cacheGet = (key) => {
  try { return JSON.parse(localStorage.getItem(CACHE_PREFIX + key) || 'null'); } catch (e) { return null; }
};

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const money = (n) => `₹${(Number(n) || 0).toFixed(2)}`;

// Receipt printed on the device, same layout as the server receipt
export function printLocalReceipt(entry) {
  const s = cacheGet('receipt_settings') || {};
  const width = String(s.receipt_paper_width || '58');
  const preview = width === '58' ? 220 : 300;
  const d = entry.display || {};
  const when = new Date(entry.client_time).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const payRows = (d.payments && d.payments.length)
    ? d.payments.map((p) => `<tr class="totals-row"><td class="totals-label">${esc(String(p.mode).toUpperCase())}</td><td class="totals-value">${money(p.amount)}</td></tr>`).join('')
    : `<tr class="totals-row"><td class="totals-label">Payment</td><td class="totals-value">${esc(String(d.payment_mode || '').toUpperCase())}</td></tr>`;
  const html = `<html><head><title>Receipt ${esc(entry.client_ref)}</title><style>
    @page { size: ${width}mm auto; margin: 0; } * { box-sizing: border-box; }
    body { font-family: -apple-system, 'Segoe UI', sans-serif; background: #EDEDED; margin: 0; padding: 30px 12px; display: flex; justify-content: center; }
    .receipt { font-family: 'Courier New', monospace; width: ${preview}px; background: #fff; padding: 20px 18px; font-size: 13px; line-height: 1.5; color: #111; }
    .shop-name { text-align: center; font-size: 16px; font-weight: 800; } .shop-meta { text-align: center; font-size: 11.5px; color: #444; }
    .bill-meta { text-align: center; font-size: 11px; margin-top: 8px; } .offline { text-align: center; font-size: 11px; font-weight: 800; margin-top: 4px; }
    .divider { border: none; border-top: 1px dashed #999; margin: 10px 0; } .divider.solid { border-top: 1.5px solid #111; }
    table { width: 100%; border-collapse: collapse; } .item-row td { padding: 3px 0; vertical-align: top; font-size: 12.5px; }
    .item-qty { text-align: center; color: #555; } .item-price { text-align: right; font-weight: 600; }
    .totals-row td { padding: 3px 0; font-size: 12.5px; } .totals-value { text-align: right; } .grand-total td { padding-top: 8px; font-size: 16px; font-weight: 800; }
    .footer { text-align: center; margin-top: 14px; font-size: 12px; font-style: italic; }
    .print-btn { display: block; width: 100%; margin-top: 20px; padding: 11px; border: none; border-radius: 8px; background: #7B61FF; color: #fff; font-weight: 700; cursor: pointer; }
    @media print { body { background: #fff; padding: 0; display: block; } .receipt { width: 100%; padding: 4mm; } .print-btn { display: none; } }
  </style></head><body><div class="receipt">
    <div class="shop-name">🍦 ${esc(s.shop_name || 'Ice Cream Shop')}</div>
    ${s.shop_address ? `<div class="shop-meta">${esc(s.shop_address)}</div>` : ''}
    ${s.shop_phone ? `<div class="shop-meta">Ph: ${esc(s.shop_phone)}</div>` : ''}
    ${s.shop_gst ? `<div class="shop-meta">GSTIN: ${esc(s.shop_gst)}</div>` : ''}
    <div class="bill-meta">${esc(entry.client_ref)}<br/>${esc(when)}</div>
    <div class="offline">OFFLINE BILL</div>
    <hr class="divider solid"/>
    <table>${(d.items || []).map((i) => `<tr class="item-row"><td>${esc(i.name)}${i.free ? ' <b>(FREE)</b>' : ''}</td><td class="item-qty">x${esc(i.qty)}</td><td class="item-price">${money(i.price)}</td></tr>`).join('')}</table>
    <hr class="divider"/>
    <table>
      <tr class="totals-row"><td>Subtotal</td><td class="totals-value">${money(d.subtotal)}</td></tr>
      ${d.discount > 0 ? `<tr class="totals-row"><td>Discount</td><td class="totals-value">-${money(d.discount)}</td></tr>` : ''}
      <tr class="grand-total"><td>Total</td><td class="totals-value">${money(d.total)}</td></tr>
    </table>
    <hr class="divider"/><table>${payRows}</table><hr class="divider solid"/>
    <div class="footer">${esc(s.receipt_footer || 'Thank you, visit again!')}</div>
    <button class="print-btn" onclick="window.print()">Print Receipt</button>
  </div><script>window.onload = function () { setTimeout(function () { window.print(); }, 200); };</script></body></html>`;
  const w = window.open('', '_blank');
  if (!w) { alert('Allow pop-ups for this site to print receipts.'); return; }
  w.document.open(); w.document.write(html); w.document.close();
}
